import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { buildProductFilterTags, ProductFilterTag, SearchResultItem } from '../GlobalSearchUtils';

export interface UseProductFilterReturn {
  selectedGlAccount: string;
  setSelectedGlAccount: (label: string) => void;
  filteredProducts: SearchResultItem[];
  glAccountTags: ProductFilterTag[];
}

const SPECIAL_FILTERS = ['All Products', 'Flagship', 'Favorites'];

export const useProductFilter = (products: SearchResultItem[]): UseProductFilterReturn => {
  const { distinctModules } = useAppContext();
  const [selectedGlAccount, setSelectedGlAccount] = useState('All Products');

  const glAccountTags = useMemo(() => buildProductFilterTags(products, distinctModules), [products, distinctModules]);

  const filteredProducts = useMemo(() => {
    if (selectedGlAccount === 'All Products') return products;
    if (selectedGlAccount === 'Flagship') return products.filter((p) => p.isFlagship);
    if (selectedGlAccount === 'Favorites') return products.filter((p) => p.isFavorite);
    if (selectedGlAccount === 'Other') return products.filter((p) => !p.glAccount || p.glAccount === 'N/A');
    return products.filter((p) => p.glAccount === selectedGlAccount);
  }, [products, selectedGlAccount]);

  useEffect(() => {
    if (filteredProducts.length === 0 && products.length > 0 && selectedGlAccount !== 'All Products') {
      setSelectedGlAccount('All Products');
      return;
    }
    const isSpecialFilter = SPECIAL_FILTERS.includes(selectedGlAccount);
    const currentTagExists = glAccountTags.some((t) => t.label === selectedGlAccount);
    if (!isSpecialFilter && !currentTagExists) {
      setSelectedGlAccount(glAccountTags[0]?.label || 'Flagship');
    }
  }, [glAccountTags, selectedGlAccount, filteredProducts.length, products.length]);

  return { selectedGlAccount, setSelectedGlAccount, filteredProducts, glAccountTags };
};
